// src/authpages/group/ManageGroupForm.js
import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  UserPlus, 
  CheckCircle, 
  UserCheck, 
  AlertCircle,
  Save,
  Trash2,
  Users
} from 'lucide-react';

const ManageGroupForm = ({ 
  group,
  friends, 
  loading, 
  error, 
  studyMethods, 
  timerMethods,
  currentUserId,
  onUpdateGroup,
  onDeleteGroup,
  onCancel
}) => {
  // Form state
  const [groupName, setGroupName] = useState(group.name || '');
  const [groupSubject, setGroupSubject] = useState(group.subject || '');
  const [selectedMethod, setSelectedMethod] = useState(group.studyMethod || '');
  const [selectedTimerMethod, setSelectedTimerMethod] = useState(group.timerMethod || '');
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [formError, setFormError] = useState('');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Initialize selected friends from current group members (excluding current user)
  useEffect(() => {
    if (group && group.members) {
      setSelectedFriends(
        group.members.filter(memberId => memberId !== currentUserId)
      );
    }
  }, [group, currentUserId]);

  // Check if user is the creator/owner of the group
  const isGroupOwner = group.creator === currentUserId;

  // Handle friend selection toggle
  const toggleFriendSelection = (friendId) => {
    setSelectedFriends(prev => {
      if (prev.includes(friendId)) {
        return prev.filter(id => id !== friendId);
      } else {
        return [...prev, friendId];
      }
    });
  };

  // Get selected timer method details
  const getSelectedTimerDetails = () => {
    if (!selectedTimerMethod) return null;
    return timerMethods.find(method => method.name === selectedTimerMethod);
  };

  const selectedTimer = getSelectedTimerDetails();

  // Form submission handler
  const handleSubmit = () => {
    // Reset any previous errors
    setFormError('');
    
    // Only require group name and subject
    if (!groupName || !groupSubject) {
      setFormError('Please fill in the group name and subject');
      return;
    }

    // Get selected timer method details if one was selected
    let timerSettings = group.timerSettings;
    if (selectedTimerMethod) {
      const timerMethod = timerMethods.find(method => method.name === selectedTimerMethod);
      if (timerMethod) {
        timerSettings = timerMethod;
      }
    }

    const updatedGroup = {
      ...group,
      name: groupName,
      subject: groupSubject,
      studyMethod: selectedMethod || 'Not specified',
      timerMethod: selectedTimerMethod || 'None',
      timerSettings: timerSettings,
      members: [currentUserId, ...selectedFriends],
      memberCount: 1 + selectedFriends.length,
      lastActive: new Date().toISOString()
    };

    // Call the parent component's handler
    onUpdateGroup(updatedGroup);
  };

  // Handle delete confirmation
  const handleDelete = () => {
    if (isDeleteConfirmOpen) {
      onDeleteGroup(group.id);
    } else {
      setIsDeleteConfirmOpen(true);
    }
  };

  // Get list of friends not already in the group
  const availableFriends = friends.filter(
    friend => !group.members.includes(friend.id) || selectedFriends.includes(friend.id)
  );

  // Get list of current members (for display)
  const currentMembers = friends.filter(
    friend => group.members.includes(friend.id) && friend.id !== currentUserId
  );

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mx-auto">
      <h2 className="text-2xl font-semibold mb-4 text-blue-600">
        Manage Study Group
      </h2>
      
      {/* Error display */}
      {(formError || error) && (
        <div className="bg-red-50 p-4 rounded-md text-red-600 mb-4 flex items-center">
          <AlertCircle className="mr-2 flex-shrink-0" size={18} />
          <p>{formError || error}</p>
        </div>
      )}
      
      {/* Group Details */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3 text-gray-700">Group Details</h3>
        <div className="space-y-4">
          <div>
            <label className="block mb-2 text-gray-700">Group Name</label>
            <input 
              type="text" 
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group name"
              className="w-full p-2 border rounded-md"
            />
          </div>
          <div>
            <label className="block mb-2 text-gray-700">Subject</label>
            <input 
              type="text" 
              value={groupSubject}
              onChange={(e) => setGroupSubject(e.target.value)}
              placeholder="What subject are you studying?"
              className="w-full p-2 border rounded-md"
            />
          </div>
        </div>
      </div>
      
      {/* Group Study Method Dropdown Selection */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3 text-gray-700">
          Group Study Method <span className="text-sm font-normal text-gray-500">(Optional)</span>
        </h3>
        <select 
          value={selectedMethod}
          onChange={(e) => setSelectedMethod(e.target.value)}
          className="w-full p-2 border rounded-md"
        >
          <option value="">Select a study method (optional)</option>
          {studyMethods.map((method, index) => (
            <option key={index} value={method.name}>
              {method.name}
            </option>
          ))}
        </select>
        
        {selectedMethod && (
          <div className="mt-2 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-gray-600">
              {studyMethods.find(m => m.name === selectedMethod)?.description || ''}
            </p>
          </div>
        )}
      </div>
      
      {/* Timer Method Dropdown Selection */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3 text-gray-700">
          Timer Method <span className="text-sm font-normal text-gray-500">(Optional)</span>
        </h3>
        <select 
          value={selectedTimerMethod}
          onChange={(e) => setSelectedTimerMethod(e.target.value)}
          className="w-full p-2 border rounded-md"
        >
          <option value="">Select a timer method (optional)</option>
          {timerMethods.map((timer, index) => (
            <option key={index} value={timer.name}>
              {timer.name}
            </option>
          ))}
        </select>
        
        {selectedTimer && (
          <div className="mt-2 p-3 bg-blue-50 rounded-md">
            <div className="flex items-center mb-2">
              <Clock className="mr-2 text-blue-500" size={18} />
              <h4 className="font-medium">{selectedTimer.name}</h4>
            </div>
            <p className="text-sm text-gray-600 mb-2">{selectedTimer.description}</p>
            <div className="text-xs text-gray-500">
              <span className="font-medium">Focus:</span> {selectedTimer.workTime}min / 
              <span className="font-medium"> Break:</span> {selectedTimer.breakTime}min
              {selectedTimer.longBreakTime && ` / `}
              {selectedTimer.longBreakTime && (
                <><span className="font-medium"> Long Break:</span> {selectedTimer.longBreakTime}min</>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Current Group Members */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3 text-gray-700">Current Members</h3>
        <div className="bg-gray-50 p-3 rounded-md mb-3">
          <div className="flex items-center mb-2">
            <Users className="mr-2 text-blue-500" size={18} />
            <h4 className="font-medium">Members ({group.memberCount})</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
              <UserCheck className="mr-1" size={14} />
              You (Owner)
            </span>
            {currentMembers.map((member) => (
              <span key={member.id} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                <UserCheck className="mr-1" size={14} />
                {member.fullName}
              </span>
            ))}
          </div>
        </div>
      </div>
      
      {/* Add More Friends */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3 text-gray-700">Add More Friends</h3>
        {loading ? (
          <p className="text-gray-600">Loading your friends...</p>
        ) : error ? (
          <div className="bg-red-50 p-3 rounded-md text-red-600 flex items-center">
            <AlertCircle className="mr-2" size={18} />
            {error}
          </div>
        ) : availableFriends.length === 0 ? (
          <div className="bg-yellow-50 p-3 rounded-md text-yellow-700">
            <p>You don't have any more friends to add to the group.</p>
          </div>
        ) : (
          <div className="bg-gray-50 p-3 rounded-md max-h-60 overflow-y-auto">
            <p className="text-sm text-gray-500 mb-3">Select friends to add to your study group:</p>
            <div className="space-y-2">
              {availableFriends.map((friend) => (
                <div 
                  key={friend.id}
                  className={`p-2 rounded-md flex items-center ${
                    selectedFriends.includes(friend.id) ? 'bg-blue-50' : 'hover:bg-gray-100'
                  }`}
                >
                  <input
                    type="checkbox"
                    id={`friend-${friend.id}`}
                    checked={selectedFriends.includes(friend.id)}
                    onChange={() => toggleFriendSelection(friend.id)}
                    className="mr-3"
                  />
                  <label htmlFor={`friend-${friend.id}`} className="flex-grow cursor-pointer">
                    <div className="font-medium">{friend.fullName}</div>
                    <div className="text-sm text-gray-500">{friend.email}</div>
                  </label>
                  <CheckCircle 
                    className={`${selectedFriends.includes(friend.id) ? 'text-blue-500' : 'text-gray-200'}`} 
                    size={20} 
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-2 text-sm text-gray-500 flex items-center">
          <UserCheck className="mr-1" size={16} />
          {selectedFriends.length} friends selected
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button 
          onClick={handleSubmit}
          className="flex-1 bg-blue-500 text-white p-3 rounded-md hover:bg-blue-600 font-medium flex items-center justify-center"
          disabled={loading}
        >
          <Save className="mr-2" size={18} />
          Save Changes
        </button>
        
        <button 
          onClick={onCancel}
          className="px-4 py-3 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700"
        >
          Cancel
        </button>
        
        {isGroupOwner && (
          <button 
            onClick={handleDelete}
            className={`px-4 py-3 rounded-md flex items-center justify-center font-medium ${
              isDeleteConfirmOpen 
                ? 'bg-red-500 text-white hover:bg-red-600' 
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            <Trash2 className="mr-2" size={18} />
            {isDeleteConfirmOpen ? 'Confirm Delete' : 'Delete Group'}
          </button>
        )}
      </div>
      
      {/* Delete confirmation */}
      {isDeleteConfirmOpen && (
        <div className="mt-4 p-3 bg-red-50 rounded-md text-red-800 text-sm">
          <p className="font-medium">Are you sure you want to delete this group?</p>
          <p>This action cannot be undone. Click "Confirm Delete" to permanently remove this group.</p>
        </div>
      )}
    </div>
  );
};

export default ManageGroupForm;